import type { Property } from "@/types/property";

/**
 * Descriptive, search-friendly URLs for property detail pages.
 *
 * Google's SEO guidance recommends URLs that contain human-readable
 * words rather than opaque identifiers. Listing IDs can themselves
 * contain hyphens (`row-abc`, `HH-2104`), so the ID is kept as its own
 * path segment and the descriptive slug follows it:
 *
 *   /properties/<id>/<beds>-<type>-<community>-for-sale
 *
 * The detail route reads only `:id`; the trailing `:slug` segment is
 * decorative and ignored, which keeps old `/properties/<id>` links
 * working while giving crawlers and users a meaningful address.
 */

type PropertyUrlFields = Pick<
  Property,
  "id" | "bedrooms" | "propertyType" | "community" | "listingType"
>;

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function listingSuffix(listingType: Property["listingType"]): string {
  if (listingType === "rent") return "for-rent";
  if (listingType === "off-plan") return "off-plan";
  return "for-sale";
}

/** Descriptive slug segment (no ID) for a property. */
export function propertySlug(property: PropertyUrlFields): string {
  const beds = property.bedrooms === 0 ? "studio" : `${property.bedrooms}-bedroom`;
  return slugify(
    `${beds} ${property.propertyType} ${property.community} ${listingSuffix(property.listingType)}`,
  );
}

/** Canonical path for a property detail page, e.g. `/properties/123/2-bedroom-apartment-downtown-dubai-for-sale`. */
export function propertyPath(property: PropertyUrlFields): string {
  return `/properties/${property.id}/${propertySlug(property)}`;
}
